import React from 'react'
import { useState, useEffect } from 'react';
import { Box, Typography, Button, Paper, TextField, Checkbox, FormControlLabel, Alert, Snackbar, CircularProgress, FormGroup, Divider } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import ApiService from '../../../services/api';

import logger from '../../../utils/logger';
const AddClient = () => {
  const navigate = useNavigate();
  const [formState, setFormState] = useState({
    full_name: '',
    email: '',
    password: '',
    phone_number: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [pages, setPages] = useState([]);
  const [selectedPageIds, setSelectedPageIds] = useState([]);

  const Container = styled(Box)(({ theme }) => ({
    padding: theme.spacing(3),
    backgroundColor: '#f8f9fa',
  }));

  const FormContainer = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(4),
    borderRadius: '12px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
    maxWidth: 720,
    margin: '0 auto',
    backgroundColor: '#fff',
  }));

  const SubmitButton = styled(Button)(({ theme }) => ({
    padding: '10px 28px',
    fontWeight: 600,
    textTransform: 'none',
    backgroundColor: '#0b91d6',
    borderRadius: '8px',
    '&:hover': { backgroundColor: '#0a7db7' },
  }));

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckbox = (e) => {
    const { name, checked } = e.target;
    setFormState((prev) => ({ ...prev, [name]: checked }));
  };

  useEffect(() => {
    const loadPages = async () => {
      try {
        const res = await ApiService.get('/admin/routes/getPage');
        logger.log(res, "res");
        
        const data = res?.data || res;
        if (Array.isArray(data)) {
          setPages(data);
          return;
        }
        if (Array.isArray(data?.data)) {
          setPages(data.data);
          return;
        }
        throw new Error('Unexpected pages response shape');
      } catch (_) {
        setPages([
          { id: 1, slug: 'leads', page_name: 'Leads', status: true },
          { id: 2, slug: 'meetings', page_name: 'Meetings', status: true },
          { id: 3, slug: 'clients', page_name: 'Clients', status: true },
          { id: 4, slug: 'employee', page_name: 'Employee', status: true },
          { id: 5, slug: 'attendance', page_name: 'Attendance', status: true },
          { id: 6, slug: 'holiday', page_name: 'Holiday', status: true },
          { id: 7, slug: 'local-citation', page_name: 'Local Citation', status: true },
        ]);
      }
    };
    loadPages();
  }, []);
  const togglePermission = (pageId) => (e) => {
    const checked = e.target.checked;
    setSelectedPageIds((prev) => {
      const set = new Set(prev);
      if (checked) set.add(pageId); else set.delete(pageId);
      return Array.from(set);
    });
  };

  const validate = () => {
    const newErrors = {};
    if (!formState.full_name?.trim()) newErrors.full_name = 'Full name is required';
    if (!formState.email?.trim()) newErrors.email = 'Email is required';
    if (!formState.password?.trim()) newErrors.password = 'Password is required';
    if (!formState.phone_number?.trim()) newErrors.phone_number = 'Phone number is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      setSnackbar({ open: true, message: 'Please fix the errors above.', severity: 'error' });
      return;
    }
    setLoading(true);
    try {
      const payload = {
        full_name: formState.full_name,
        email: formState.email,
        password: formState.password,
        phone_number: formState.phone_number,
        page: selectedPageIds.map((id) => ({ page_id: id })),
      };
      const response = await ApiService.post('/admin/clients/add-client', payload);
      if (response) {
        setSnackbar({ open: true, message: 'Client added successfully!', severity: 'success' });
        setTimeout(() => navigate('/admin-dashboard'), 800);
        setFormState({ full_name: '', email: '', password: '', phone_number: '' });
        setSelectedPageIds([]);
      } else {
        throw new Error('Failed to add client');
      }
    } catch (error) {
      const raw = error?.response?.data || error?.body || error?.message;
      const backendMsg = typeof raw === 'string' ? raw : (raw?.message || raw?.error || JSON.stringify(raw));
      setSnackbar({ open: true, message: backendMsg || 'Failed to add client. Please try again.', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

  return (
    <Container>
      <Typography variant="h4" fontWeight={700} align="left" gutterBottom>
        Add Clients
      </Typography>
      <Typography variant="body2" color="text.secondary" align="left" sx={{ mb: 4 }}>
        Create a new client by filling the information below.
      </Typography>

      <FormContainer>
        <form onSubmit={handleSubmit} noValidate>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
            Basic Information
          </Typography>

          <TextField
            fullWidth
            label="Full Name"
            name="full_name"
            value={formState.full_name}
            onChange={handleChange}
            required
            error={!!errors.full_name}
            helperText={errors.full_name}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Email"
            name="email"
            type="email"
            value={formState.email}
            onChange={handleChange}
            required
            error={!!errors.email}
            helperText={errors.email}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Password"
            name="password"
            type="password"
            value={formState.password}
            onChange={handleChange}
            required
            error={!!errors.password}
            helperText={errors.password}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Phone Number"
            name="phone_number"
            value={formState.phone_number}
            onChange={handleChange}
            required
            error={!!errors.phone_number}
            helperText={errors.phone_number}
            sx={{ mb: 2 }}
          />

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
            Permissions
          </Typography>
          {pages.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Loading available pages...
            </Typography>
          ) : (
            <FormGroup row sx={{ mb: 2 }}>
              {pages
                .filter((p) => p?.status !== false)
                .map((p) => (
                  <FormControlLabel
                    key={p.id}
                    control={
                      <Checkbox
                        checked={selectedPageIds.includes(p.id)}
                        onChange={togglePermission(p.id)}
                      />
                    }
                    label={p.page_name}
                    sx={{ mr: 3 }}
                  />
                ))}
            </FormGroup>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button variant="outlined" color="error" onClick={() => navigate(-1)} disabled={loading}>
              Cancel
            </Button>
            <SubmitButton type="submit" variant="contained" disabled={loading} startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}>
              {loading ? 'Submitting...' : 'Add Client'}
            </SubmitButton>
          </Box>
        </form>

        <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </FormContainer>
    </Container>
  )
}
export default AddClient